import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Gavel from '@mui/icons-material/Gavel';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import HomeWork from '@mui/icons-material/HomeWork';
import { useTheme } from '@material-ui/core/styles';
import { readableHeading } from '../readablePalette';

const cardSx = (theme) => ({
  height: '100%',
  p: { xs: 2.5, sm: 3 },
  borderRadius: 3,
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid rgba(196, 164, 93, 0.45)`,
  bgcolor: theme.palette.mode === 'dark' ? 'rgba(21, 37, 30, 0.85)' : theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 12px 40px rgba(0,0,0,0.35)'
      : '0 16px 48px rgba(27, 51, 38, 0.08)',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 18px 48px rgba(0,0,0,0.45)'
        : '0 22px 56px rgba(27, 51, 38, 0.12)',
  },
});

const EditorialFocus = () => {
  const theme = useTheme();
  const h = readableHeading(theme);
  const deep = theme.palette.primary.main;
  const gold = theme.palette.secondary.main;

  return (
    <Box
      component="section"
      sx={{
        my: { xs: 5, md: 7 },
        py: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: 0, sm: 4 },
        position: 'relative',
        overflow: 'hidden',
        background: (t) =>
          t.palette.mode === 'dark'
            ? `linear-gradient(165deg, rgba(196,164,93,0.07) 0%, transparent 42%), linear-gradient(180deg, ${t.palette.background.default} 0%, rgba(15,26,20,0.98) 100%)`
            : `linear-gradient(165deg, rgba(196,164,93,0.12) 0%, transparent 40%), linear-gradient(180deg, #f7faf7 0%, #fff 55%)`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(196,164,93,0.2) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                height: '100%',
                minHeight: { xs: 'auto', md: 220 },
                maxWidth: { md: 380 },
                borderRadius: 3,
                py: { xs: 2.25, sm: 2.5 },
                pr: { xs: 2, sm: 2.25 },
                pl: { xs: 3, sm: 4, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                background: `linear-gradient(145deg, ${deep} 0%, ${theme.palette.primary.dark} 100%)`,
                color: '#fff',
                boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
                border: `1px solid rgba(196, 164, 93, 0.35)`,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.45,
                    borderRadius: 999,
                    bgcolor: 'rgba(196,164,93,0.2)',
                    border: `1px solid rgba(196,164,93,0.5)`,
                    mb: 1.5,
                  }}
                >
                  <AutoAwesome sx={{ fontSize: 16, color: gold }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.12em', color: gold, fontSize: '0.68rem' }}>
                    Estrategia de contenido
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontWeight: 700,
                    lineHeight: 1.28,
                    fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.4rem' },
                  }}
                >
                  Su firma, al centro del hogar que protege lo que importa
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.12)',
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.92, lineHeight: 1.65, display: 'block', fontSize: '0.72rem' }}>
                  <strong>Legalmente en Familia</strong> · lunes 12:00 PM · TNO Network
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{
                lineHeight: 1.8,
                fontSize: '1.05rem',
                mb: 0,
              }}
            >
              En <strong>Legalmente en Familia</strong> la audiencia busca respuestas concretas: cómo cuidar a los suyos,
              qué hacer ante un conflicto y cómo moverse con seguridad en un mundo cada vez más digital. La presencia de{' '}
              <strong>Hoet & Partners</strong> se articula en torno al eje de{' '}
              <Box component="span" sx={{ color: gold, fontWeight: 700 }}>
                seguridad patrimonial y familiar
              </Box>
              —el mismo que ya guía parte del programa—, de modo que su mensaje se perciba como{' '}
              <Box component="em" sx={{ color: h, fontStyle: 'italic' }}>
                acompañamiento de excelencia
              </Box>
              , no como un aviso aislado.
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: { xs: 2, md: 3 } }}>
          <Grid item xs={12} md={6}>
            <Box sx={cardSx(theme)}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  background: `linear-gradient(135deg, ${gold}33, transparent)`,
                  color: gold,
                  border: `1px solid rgba(196,164,93,0.4)`,
                }}
              >
                <HomeWork sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="overline" sx={{ color: gold, fontWeight: 700, letterSpacing: '0.12em' }}>
                En antena
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: h, mt: 0.5, mb: 1.25, fontFamily: 'Georgia, serif', lineHeight: 1.3 }}>
                Tres frentes donde su expertise cuenta
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: h }}>
                    Marca y negocio familiar.
                  </Box>{' '}
                  Qué blindar primero y qué riesgos aparecen cuando el negocio es de la casa.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: h }}>
                    Autor y redes.
                  </Box>{' '}
                  Qué implica compartir contenido en el móvil, en grupos familiares y en la vida online de los jóvenes.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: h }}>
                    Herencia intangible.
                  </Box>{' '}
                  Activos digitales y conocimiento: cómo hablarlo sin abrumar a quien escucha.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={cardSx(theme)}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  background: `linear-gradient(135deg, ${gold}33, transparent)`,
                  color: gold,
                  border: `1px solid rgba(196,164,93,0.4)`,
                }}
              >
                <Gavel sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="overline" sx={{ color: gold, fontWeight: 700, letterSpacing: '0.12em' }}>
                Cómo lo contamos
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: h, mt: 0.5, mb: 1.25, fontFamily: 'Georgia, serif', lineHeight: 1.3 }}>
                PI cercana, sin perder rigor
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: h }}>
                    Del tecnicismo al gesto concreto.
                  </Box>{' '}
                  La propiedad intelectual en palabras que la familia y el emprendedor reconocen al instante.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: h }}>
                    Dos voces, un mismo estándar.
                  </Box>{' '}
                  La Dra. Davogustto da el tono humano; Hoet & Partners aporta el criterio legal que respalda cada idea.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: h }}>
                    Qué hacer mañana.
                  </Box>{' '}
                  Menos teoría suelta: pasos claros para quien hoy necesita decidir con información, no con miedo.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default EditorialFocus;
